/**
 * The warning detail's map and area list (`GET /api/warnings/<id>/areas/`),
 * shared by both apps: which area comes first, how many are listed, what the
 * map is framed on, its shapes as react-native-maps rings, and what a screen
 * reader hears for it. Each app owns the drawing (react-native-maps on mobile,
 * react-map-gl on the webapp), the expanded/collapsed state and the styles.
 *
 * Design: `.scratch/weather-warnings-redesign/target.html` (`map_area` in
 * `gen_target.py`). Pure: `intl` and `locale` are parameters (see `./view`).
 *
 * Imports `./view`, never the utils barrel (`importCycles.test.ts`).
 */
import {messages} from '@smartmixin/i18n';

import type {
  GeoJsonPosition,
  WarningAreaFeature,
  WarningAreaGeometry,
  WarningAreasResponse,
  WarningBbox,
} from '../../api/types';
import {MAX_AREA_ROWS, WarningsIntl, WarningsViewParams} from './view';

// ------------------------------------------------------------------ order

const nameOf = (feature: WarningAreaFeature): string =>
  (feature.properties?.name ?? '').trim();

const isOwn = (feature: WarningAreaFeature, own: string | null): boolean =>
  (own !== null && feature.id === own) || feature.properties?.own === true;

/**
 * The area the point is in, as the backend picks it: the top-level `own`, the
 * SMALLEST area containing the point. `properties.own` is true on EVERY area
 * containing it (nested or overlapping areas), so it never decides which one
 * is highlighted, framed or heads the list. Null without a top-level `own`.
 */
const primaryOwn = (
  features: readonly WarningAreaFeature[],
  own: string | null,
): WarningAreaFeature | null =>
  own === null ? null : (features.find((f) => f.id === own) ?? null);

const collatorFor = (locale: string): Intl.Collator => {
  try {
    return new Intl.Collator(locale, {numeric: true});
  } catch {
    // A malformed tag (e.g. "en_US") throws a RangeError; sort anyway.
    return new Intl.Collator(undefined, {numeric: true});
  }
};

/**
 * The areas as the list reads them: the own area (the top-level `own`) first,
 * then any other area flagged `own` (one it nests in or overlaps, or a point
 * on a shared border), then the
 * rest, each group in the reader's collation with numbers read as numbers
 * ("Zone 2" before "Zone 10"). An area with no name goes last, even the own
 * one; equal names keep a stable order by id. Returns a new array.
 */
export const orderAreas = (
  features: readonly WarningAreaFeature[],
  own: string | null,
  locale: string,
): WarningAreaFeature[] => {
  const collator = collatorFor(locale);
  const byName = (a: WarningAreaFeature, b: WarningAreaFeature) =>
    collator.compare(nameOf(a), nameOf(b)) || collator.compare(a.id, b.id);
  const primary = primaryOwn(features, own);
  // A nameless area has nothing to show, own or not: it goes last.
  const rank = (f: WarningAreaFeature) =>
    !nameOf(f) ? 3 : f === primary ? 0 : isOwn(f, own) ? 1 : 2;
  return [...features].sort((a, b) => rank(a) - rank(b) || byName(a, b));
};

// ------------------------------------------------------------------ list

export type WarningAreaRow = {id: string; name: string};

export type WarningAreaListViewModel = {
  /**
   * "Prelitoral de Girona (this location)" + "+ 38 other areas", heading the
   * list; null when the point is in none of the areas (or none was given), or
   * its area has no name to show.
   */
  own: {id: string; name: string; others: string | null} | null;
  /** Every other area, in order. */
  rows: WarningAreaRow[];
  /** The first `MAX_AREA_ROWS` of `rows`: the list before "Show all". */
  collapsedRows: WarningAreaRow[];
  /** "Show all 39 areas" when `rows` is longer than the collapsed list. */
  showAll: string | null;
  /**
   * The map's zoom-out control, "Show all 39 areas on the map": named apart
   * from `showAll` so the two controls never sound alike. Null for one area.
   */
  showAllOnMap: string | null;
  /** The map's text alternative (`mapLabel`). */
  mapLabel: string;
  /**
   * In place of the map when it cannot be drawn (no area has a shape, or the
   * map fails): "Map unavailable. The 39 areas are listed below."
   */
  mapUnavailable: string;
  /**
   * How many areas the list holds once expanded. The features, not
   * `areas_count`: "Show all 39 areas" must reveal 39 rows.
   */
  total: number;
};

export type WarningAreasParams = {
  intl: WarningsIntl;
  /** The reader's locale, for the collation, e.g. "sv". */
  locale: string;
  place?: WarningsViewParams['place'];
};

/**
 * The area list under the map: own area first, then 6 others, then "Show all
 * N areas" (spec: `rest[:6]`, the button only when more remain).
 */
export const areaListViewModel = (
  response: WarningAreasResponse,
  {intl, locale, place = 'location'}: WarningAreasParams,
): WarningAreaListViewModel => {
  const features = response.areas.features;
  const ordered = orderAreas(features, response.own, locale);
  const total = ordered.length;
  const owner = primaryOwn(features, response.own);
  // Agencies name every area; a blank one cannot head the list as "(this
  // location)", so it is listed (last) like any other.
  const primary = owner && nameOf(owner) ? owner : null;
  const ownName = (feature: WarningAreaFeature) =>
    intl.formatMessage(
      place === 'station'
        ? messages.weatherWarnings.areaYourStation
        : messages.weatherWarnings.areaThisLocation,
      {area: nameOf(feature)},
    );
  const rows = ordered
    .filter((f) => f !== primary)
    .map((f) => ({
      id: f.id,
      name: isOwn(f, response.own) && nameOf(f) ? ownName(f) : nameOf(f),
    }));
  return {
    own: primary
      ? {
          id: primary.id,
          name: ownName(primary),
          others:
            total > 1
              ? intl.formatMessage(messages.weatherWarnings.otherAreas, {
                  count: total - 1,
                })
              : null,
        }
      : null,
    rows,
    collapsedRows: rows.slice(0, MAX_AREA_ROWS),
    showAll:
      rows.length > MAX_AREA_ROWS
        ? intl.formatMessage(messages.weatherWarnings.showAllAreas, {
            count: total,
          })
        : null,
    showAllOnMap:
      total > 1
        ? intl.formatMessage(messages.weatherWarnings.showAllOnMap, {
            count: total,
          })
        : null,
    mapLabel: mapLabel(response, {intl}),
    mapUnavailable: intl.formatMessage(
      messages.weatherWarnings.mapUnavailable,
      {count: total},
    ),
    total,
  };
};

/**
 * The map's text alternative: how many areas, and which one is highlighted.
 * It claims a highlight only when the own area has a name to say AND a shape
 * the map draws (some NOAA zones have none); otherwise "Map of the 39 areas
 * covered." The count is every area the warning covers, as the list shows
 * them, drawn or not.
 */
export const mapLabel = (
  response: WarningAreasResponse,
  {intl}: {intl: WarningsIntl},
): string => {
  const features = response.areas.features;
  const count = features.length;
  const primary = primaryOwn(features, response.own);
  const highlighted =
    primary && toLatLngRings(primary.geometry).length > 0
      ? nameOf(primary)
      : '';
  // One area: "Map of the area covered: X." claims no highlight.
  const area = highlighted || (count === 1 ? nameOf(features[0]) : '');
  return area
    ? intl.formatMessage(messages.weatherWarnings.mapLabel, {count, area})
    : intl.formatMessage(messages.weatherWarnings.mapLabelNoHighlight, {
        count,
      });
};

// ------------------------------------------------------------------ camera

/**
 * The own area is framed on its own when, with every area fitted, it would
 * cover less than 24×24 px (spec `ZOOM_MIN_PX`, compared as an AREA:
 * `width × height < 24²`, as `gen_target.py` does).
 */
export const OWN_AREA_MIN_PX = 24;
/** Framed on its own, the own area spans this share of the map (spec `ZOOM_SHARE`). */
export const OWN_AREA_SHARE = 0.22;

/** Web Mercator's latitude limit. */
const MAX_LAT = 85.0511287798;
const RAD = Math.PI / 180;

const mercX = (lon: number) => lon * RAD;
const mercY = (lat: number) => {
  const phi = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat)) * RAD;
  return Math.log(Math.tan(Math.PI / 4 + phi / 2));
};
const lonOf = (x: number) => x / RAD;
const latOf = (y: number) =>
  Math.max(
    -MAX_LAT,
    Math.min(MAX_LAT, (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / RAD),
  );

/**
 * Finite, and west <= east, south <= north. No antimeridian wrap is assumed:
 * the contract's bbox is a PostGIS extent (min/max of the coordinates), so a
 * west > east box is malformed, not a box across 180°.
 */
const usableBbox = (
  bbox: WarningBbox | null | undefined,
): bbox is WarningBbox =>
  Array.isArray(bbox) &&
  bbox.length === 4 &&
  bbox.every((v) => typeof v === 'number' && Number.isFinite(v)) &&
  bbox[0] <= bbox[2] &&
  bbox[1] <= bbox[3];

export type WarningMapViewport = {
  /** The map's size in px (CSS px on the web, dp on mobile), less any fit padding. */
  width: number;
  height: number;
};

export type WarningMapCamera = {
  /** `all`: every area fitted. `own`: framed on the own area. */
  mode: 'all' | 'own';
  /** `[west, south, east, north]` for the app's fit-bounds call. */
  bounds: WarningBbox;
};

/**
 * The map's opening camera. Fits every area (`bbox`), unless the own area
 * would then render smaller than 24×24 px: then it frames the own area at
 * `OWN_AREA_SHARE` of the map, centred on it, and the app offers
 * `showAllOnMap` to zoom out. Measured in Web Mercator, the projection both
 * map libraries draw in. Never zooms OUT: a long sliver under 576 px² whose
 * frame would be larger than the whole set stays at fit-all.
 *
 * The frame's latitudes stay within Web Mercator's ±85.05°. Its longitudes
 * are not wrapped: framing an own area close to 180° can give one past ±180°
 * (how either map library takes that is unverified).
 *
 * Null when there is nothing to fit: no usable bbox, a zero-size one, or no
 * viewport.
 */
export const mapCamera = (
  viewport: WarningMapViewport,
  bbox: WarningBbox | null,
  ownBbox: WarningBbox | null,
): WarningMapCamera | null => {
  const {width, height} = viewport;
  if (!(width > 0 && height > 0) || !usableBbox(bbox)) {
    return null;
  }
  const [w, s, e, n] = bbox;
  const dx = mercX(e) - mercX(w);
  const dy = mercY(n) - mercY(s);
  if (!(dx > 0 || dy > 0)) {
    return null;
  }
  const all: WarningMapCamera = {mode: 'all', bounds: bbox};
  if (!usableBbox(ownBbox)) {
    return all;
  }
  const [ow, os, oe, on] = ownBbox;
  const odx = mercX(oe) - mercX(ow);
  const ody = mercY(on) - mercY(os);
  if (!(odx > 0 && ody > 0)) {
    return all;
  }
  // px per Mercator unit with every area fitted (fit-bounds' scale).
  const scale = Math.min(width / dx, height / dy);
  if (odx * scale * (ody * scale) >= OWN_AREA_MIN_PX ** 2) {
    return all;
  }
  // The frame has the map's aspect, the own area spanning OWN_AREA_SHARE of
  // it along whichever side binds.
  const spanX = Math.max(odx, (ody * width) / height) / OWN_AREA_SHARE;
  const spanY = (spanX * height) / width;
  if (spanX * scale >= width) {
    return all;
  }
  const cx = (mercX(ow) + mercX(oe)) / 2;
  const cy = (mercY(os) + mercY(on)) / 2;
  return {
    mode: 'own',
    bounds: [
      lonOf(cx - spanX / 2),
      latOf(cy - spanY / 2),
      lonOf(cx + spanX / 2),
      latOf(cy + spanY / 2),
    ],
  };
};

// ------------------------------------------------------------------ shapes

export type LatLng = {latitude: number; longitude: number};

/** One polygon as react-native-maps' `<Polygon coordinates holes>` takes it. */
export type WarningAreaPolygon = {coordinates: LatLng[]; holes: LatLng[][]};

/** GeoJSON's minimum: three corners and the closing repeat of the first. */
const MIN_RING_POSITIONS = 4;

const toRing = (ring: GeoJsonPosition[]): LatLng[] =>
  (Array.isArray(ring) ? ring : [])
    .filter(
      (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]),
    )
    .map(([longitude, latitude]) => ({latitude, longitude}));

const toPolygon = (rings: GeoJsonPosition[][]): WarningAreaPolygon | null => {
  const [outer, ...holes] = (Array.isArray(rings) ? rings : []).map(toRing);
  if (!outer || outer.length < MIN_RING_POSITIONS) {
    return null;
  }
  return {
    coordinates: outer,
    holes: holes.filter((h) => h.length >= MIN_RING_POSITIONS),
  };
};

/**
 * A GeoJSON area shape as react-native-maps polygons: GeoJSON's
 * `[lon, lat]` positions become `{latitude, longitude}`, a Polygon's first
 * ring is the outline and the rest its holes, and a MultiPolygon gives one
 * polygon per member. An area with no shape (`geometry: null`), a geometry
 * type that is not a polygon, and a ring too short to be one give nothing.
 */
export const toLatLngRings = (
  geometry: WarningAreaGeometry | null,
): WarningAreaPolygon[] => {
  if (!geometry) {
    return [];
  }
  const members =
    geometry.type === 'Polygon'
      ? [geometry.coordinates]
      : geometry.type === 'MultiPolygon'
        ? geometry.coordinates
        : [];
  return members
    .map(toPolygon)
    .filter((p): p is WarningAreaPolygon => p !== null);
};
